import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

const PRIO = { High: "#EF4444", Medium: "#F59E0B", Low: "#06B6D4" };
const TYPE_ICON = { Call: "Phone", "Follow-up": "PhoneCall", Review: "PieChart", Engagement: "Gift", Content: "Megaphone", Campaign: "Send", Compliance: "ShieldCheck", General: "ListChecks" };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("Today");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", type: "Call", priority: "Medium", due: todayStr() });

  const load = useCallback(() => api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const t0 = todayStr();
  const filtered = useMemo(() => {
    if (view === "Completed") return tasks.filter((t) => t.done);
    const open = tasks.filter((t) => !t.done);
    if (view === "Today") return open.filter((t) => (t.due || "") === t0);
    if (view === "Upcoming") return open.filter((t) => (t.due || "") > t0);
    if (view === "Overdue") return open.filter((t) => t.due && t.due < t0);
    return open;
  }, [tasks, view, t0]);
  const counts = useMemo(() => {
    const open = tasks.filter((t) => !t.done);
    return {
      Today: open.filter((t) => (t.due || "") === t0).length,
      Upcoming: open.filter((t) => (t.due || "") > t0).length,
      Overdue: open.filter((t) => t.due && t.due < t0).length,
      Completed: tasks.filter((t) => t.done).length,
    };
  }, [tasks, t0]);

  const toggle = async (t) => { setTasks((ts) => ts.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x))); await api.patch(`/tasks/${t.id}`, { done: !t.done }); };
  const remove = async (id) => { setTasks((ts) => ts.filter((x) => x.id !== id)); await api.delete(`/tasks/${id}`); };
  const add = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const { data } = await api.post("/tasks", form); setTasks((ts) => [data, ...ts]); setAdding(false);
    setForm({ title: "", type: "Call", priority: "Medium", due: todayStr() }); toast.success("Task added");
  };
  const generate = async () => { const { data } = await api.post("/tasks/generate"); setTasks(data.tasks); toast.success(data.created ? `${data.created} AI tasks added` : "You're all caught up"); };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Tasks" subtitle="Your day, organised. AI turns client signals into clear next actions." icon="ListChecks" color="#8B5CF6"
        action={<div className="flex gap-2"><Btn variant="outline" onClick={generate} data-testid="tasks-generate-btn"><Icon name="Sparkles" size={15} /> AI suggest</Btn><Btn onClick={() => setAdding(true)} data-testid="task-add-btn"><Icon name="Plus" size={16} /> New Task</Btn></div>} />

      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {["Today", "Upcoming", "Overdue", "Completed"].map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`tasks-view-${v.toLowerCase()}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap border flex items-center gap-2 ${view === v ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>
            {v} <span className={`text-[11px] rounded-full px-1.5 ${view === v ? "bg-white/25" : "bg-gray-100"}`}>{counts[v]}</span>
          </button>
        ))}
      </div>

      {adding && (
        <Card className="mb-5">
          <div className="grid sm:grid-cols-4 gap-3">
            <input placeholder="Task title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" className={`${inp} sm:col-span-2`} />
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inp}>{Object.keys(TYPE_ICON).map((s) => <option key={s}>{s}</option>)}</select>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inp}>{["High", "Medium", "Low"].map((s) => <option key={s}>{s}</option>)}</select>
            <input type="date" value={form.due} onChange={(e) => setForm({ ...form, due: e.target.value })} className={inp} />
          </div>
          <div className="flex gap-2 mt-3"><Btn onClick={add} data-testid="task-save-btn">Save Task</Btn><Btn variant="outline" onClick={() => setAdding(false)}>Cancel</Btn></div>
        </Card>
      )}

      <div className="space-y-2" data-testid="tasks-list">
        {filtered.length ? filtered.map((t) => (
          <div key={t.id} className="card-mfx p-4 flex items-center gap-3" data-testid="task-row">
            <button onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className={`w-5 h-5 rounded-md border grid place-items-center shrink-0 ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-gray-300"}`}>
              {t.done && <Icon name="Check" size={13} />}
            </button>
            <div className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: `${PRIO[t.priority]}18` }}><Icon name={TYPE_ICON[t.type] || "ListChecks"} size={15} style={{ color: PRIO[t.priority] }} /></div>
            <div className="min-w-0">
              <div className={`font-medium text-sm ${t.done ? "line-through text-gray-400" : ""}`}>{t.title}</div>
              <div className="text-xs text-gray-400 flex items-center gap-2">{t.type} · <span style={{ color: PRIO[t.priority] }}>{t.priority}</span>{t.due && <><span>·</span><Icon name="Calendar" size={11} />{t.due}</>}{t.source === "ai" && <span className="text-violet-500 flex items-center gap-0.5"><Icon name="Sparkles" size={10} />AI</span>}</div>
            </div>
            <button onClick={() => remove(t.id)} className="ml-auto text-gray-300 hover:text-red-500 p-1" data-testid={`task-del-${t.id}`}><Icon name="Trash2" size={15} /></button>
          </div>
        )) : <div className="text-center text-gray-400 py-12"><Icon name="CheckCircle2" size={32} className="mx-auto mb-2 text-gray-300" />Nothing here. {view === "Today" && "Tap 'AI suggest' to plan your day."}</div>}
      </div>
    </div>
  );
}
const inp = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400";
